import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, PaperPlaneRight, X, Truck } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = {
  role: "assistant",
  content: "Hey! I'm the FreightPilot AI guide. Ask me anything — pricing, how the dispatch automation works, or how fast you'd get paid. 🚛",
};

const QUICK_REPLIES = [
  "What does it cost?",
  "How does the AI dispatch work?",
  "Will it work for 1 truck?",
  "How fast do I get paid?",
];

function sid() {
  let s = localStorage.getItem("fp_chat_sid");
  if (!s) {
    s = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("fp_chat_sid", s);
  }
  return s;
}

function chatKey() {
  let k = localStorage.getItem("fp_chat_key");
  if (!k) {
    k = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("fp_chat_key", k);
  }
  return k;
}

export const LeadChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const loadedRef = useRef(false);
  const aliveRef = useRef(true);
  const bottomRef = useRef(null);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    fetch(`${API}/api/public/lead-chat/${sid()}/history?key=${encodeURIComponent(chatKey())}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMsgs([GREETING, ...d.messages]);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || busy) return;
    if (typeof preset === "string") {
      fetch(`${API}/api/public/lead-chat/quick-tap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: preset }),
      }).catch(() => {});
    }
    setInput("");
    setBusy(true);
    setMsgs((p) => [...p, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/public/lead-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid(), message: text, session_key: chatKey() }),
      });
      if (!res.ok) throw new Error("bad status");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done || !aliveRef.current) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const ev = JSON.parse(payload);
          const chunk = ev.delta || ev.error || "";
          if (chunk) {
            setMsgs((p) => {
              const next = [...p];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + chunk };
              return next;
            });
          }
        }
      }
    } catch {
      if (aliveRef.current) {
        setMsgs((p) => {
          const next = [...p];
          next[next.length - 1] = { role: "assistant", content: "Something hiccuped — give that another try." };
          return next;
        });
      }
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="lead-chat-panel"
          className="fixed bottom-36 right-4 sm:bottom-24 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-[370px] rounded-2xl border border-white/10 bg-[#0D0D0D] shadow-2xl shadow-black/60 overflow-hidden"
        >
          <div className="flex items-center gap-2.5 bg-[#151515] px-4 py-3 border-b border-white/10">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FF5A1F]/15">
              <Truck size={16} weight="fill" color="#FF5A1F" />
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-white leading-tight">FreightPilot AI Guide</div>
              <div className="text-[10px] text-emerald-400">● Online — answers in seconds</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} data-testid="lead-chat-close-btn"
              className="ml-auto rounded-full p-1.5 text-neutral-400 hover:bg-white/10 hover:text-white transition-colors">
              <X size={16} weight="bold" />
            </button>
          </div>

          <div className="h-[340px] overflow-y-auto px-3.5 py-3 space-y-2.5" data-testid="lead-chat-messages">
            {msgs.map((m, idx) => (
              <div key={idx} data-testid={`lead-chat-message-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#FF5A1F] text-black font-medium rounded-br-md"
                    : "bg-white/[0.06] text-neutral-100 rounded-bl-md"
                }`}>
                {m.content || <span className="inline-flex gap-1 align-middle">
                  <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 animate-bounce" />
                  <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 animate-bounce [animation-delay:120ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 animate-bounce [animation-delay:240ms]" />
                </span>}
              </div>
            ))}
            {msgs.length === 1 && !busy && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="lead-chat-quick-replies">
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    data-testid={`lead-chat-quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                    className="rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-3 py-1.5 text-[12px] font-medium text-[#FF9764] transition-colors hover:bg-[#FF5A1F]/25"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="flex items-center gap-2 border-t border-white/10 bg-[#101010] px-3 py-2.5">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about pricing, dispatch, invoicing…"
              data-testid="lead-chat-input"
              className="min-w-0 flex-1 rounded-full bg-white/[0.06] border border-white/10 px-4 py-2 text-[13px] text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/60"
            />
            <button type="button" onClick={send} disabled={busy || !input.trim()} data-testid="lead-chat-send-btn"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FF5A1F] text-black transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100">
              <PaperPlaneRight size={15} weight="fill" />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="lead-chat-toggle-btn"
        aria-label="Chat with FreightPilot AI"
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#FF5A1F] text-black shadow-lg shadow-[#FF5A1F]/30 transition-transform hover:scale-110"
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircleDots size={24} weight="fill" />}
      </button>
    </>
  );
};
